'use client';

import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { 
  loadTranslations, 
  translate, 
  translateBatch, 
  TranslationKey, 
  TranslationParams, 
  TranslationValues 
} from './translate';

interface UseTranslationReturn {
  t: (key: TranslationKey, params?: TranslationParams) => string;
  tBatch: (keys: TranslationKey[], params?: TranslationParams) => Record<TranslationKey, string>;
  isLoading: boolean;
  currentLanguage: string;
  changeLanguage: (languageCode: string) => void;
}

export const useTranslation = (): UseTranslationReturn => {
  const { currentLanguage, changeLanguage: contextChangeLanguage, isLoading: contextLoading } = useLanguage();
  const [translations, setTranslations] = useState<TranslationValues>({});
  const [fallbackTranslations, setFallbackTranslations] = useState<TranslationValues>({});
  const [isLoading, setIsLoading] = useState(true);

  // 加载翻译文件
  useEffect(() => {
    const loadLanguageTranslations = async () => {
      setIsLoading(true);
      
      try {
        // 并行加载当前语言和fallback语言的翻译
        const [currentTranslations, englishTranslations] = await Promise.all([
          loadTranslations(currentLanguage.code),
          currentLanguage.code !== 'en' ? loadTranslations('en') : Promise.resolve({})
        ]);

        setTranslations(currentTranslations);
        setFallbackTranslations(englishTranslations);
      } catch (error) {
        console.error('Failed to load translations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!contextLoading) {
      loadLanguageTranslations();
    }
  }, [currentLanguage.code, contextLoading]);

  // 翻译函数
  const t = useCallback((key: TranslationKey, params?: TranslationParams): string => {
    return translate(key, params, translations, fallbackTranslations);
  }, [translations, fallbackTranslations]);

  // 批量翻译函数
  const tBatch = useCallback((keys: TranslationKey[], params?: TranslationParams): Record<TranslationKey, string> => {
    return translateBatch(keys, params, translations, fallbackTranslations);
  }, [translations, fallbackTranslations]);

  return {
    t,
    tBatch,
    isLoading: contextLoading || isLoading,
    currentLanguage: currentLanguage.code,
    changeLanguage: contextChangeLanguage,
  };
};